import { memo } from 'react';
import { ChevronDown, SlidersHorizontal, FolderOpen, FolderHeart, Lock, Users, Globe, Sparkles, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { CombinedCollectionFilter } from '@/components/collections/CollectionsList';

export type ViewMode = 'grid' | 'list';

interface SavesTabNavProps {
  readonly activeView: 'saved' | 'collections' | 'for-you';
  readonly onViewChange: (view: 'saved' | 'collections' | 'for-you') => void;
  readonly savedFilterLabel: string;
  readonly onOpenFilterSheet: () => void;
  readonly hasItems: boolean;
  readonly collectionsFilter: CombinedCollectionFilter;
  readonly onCollectionsFilterChange: (filter: CombinedCollectionFilter) => void;
  readonly collectionsCount: number;
  readonly followingCount: number;
  readonly collectionTypeCounts?: {
    private: number;
    collaborative: number;
    public: number;
  };
  readonly viewMode: ViewMode;
  readonly onViewModeChange: (mode: ViewMode) => void;
}

export const SavesTabNav = memo(function SavesTabNav({
  activeView,
  onViewChange,
  savedFilterLabel,
  onOpenFilterSheet,
  hasItems,
  collectionsFilter,
  onCollectionsFilterChange,
  collectionsCount,
  followingCount,
  collectionTypeCounts,
  viewMode,
  onViewModeChange,
}: SavesTabNavProps) {
  const getFilterLabel = () => {
    switch (collectionsFilter) {
      case 'all': return 'All';
      case 'mine': return 'Mine';
      case 'mine-private': return 'Private';
      case 'mine-collaborative': return 'Group';
      case 'mine-public': return 'Public';
      case 'following': return 'Following';
      default: return 'All';
    }
  };

  const getFilterIcon = () => {
    switch (collectionsFilter) {
      case 'mine': return <FolderOpen className="w-4 h-4" />;
      case 'mine-private': return <Lock className="w-4 h-4" />;
      case 'mine-collaborative': return <Users className="w-4 h-4" />;
      case 'mine-public': return <Globe className="w-4 h-4" />;
      case 'following': return <FolderHeart className="w-4 h-4" />;
      default: return <FolderOpen className="w-4 h-4" />;
    }
  };

  return (
    <div className="flex-shrink-0 overflow-x-auto scrollbar-hide -mx-5">
      <div className="flex items-center justify-between px-5">
        <div className="flex gap-6 shrink-0">
          <TabButton
            active={activeView === 'saved'}
            onClick={() => onViewChange('saved')}
            label="Saves"
          />
          <TabButton
            active={activeView === 'collections'}
            onClick={() => onViewChange('collections')}
            label="Collections"
          />
          <TabButton
            active={activeView === 'for-you'}
            onClick={() => onViewChange('for-you')}
            label="For You"
            icon={<Sparkles className="w-3.5 h-3.5" />}
          />
        </div>
        
        <div className="flex items-center gap-2">
          {/* Smart Filter Button - only on Saved tab */}
          {activeView === 'saved' && hasItems && (
            <button 
              onClick={onOpenFilterSheet}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-2 text-sm font-medium text-muted-foreground active:text-foreground transition-colors max-w-[130px]"
            >
              <SlidersHorizontal className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{savedFilterLabel}</span>
              <ChevronDown className="w-3.5 h-3.5 shrink-0" />
            </button>
          )}

          {/* Collections Filter Dropdown - only on Collections tab */}
          {activeView === 'collections' && (
            <CollectionsFilterDropdown
              filter={collectionsFilter}
              onFilterChange={onCollectionsFilterChange}
              collectionsCount={collectionsCount}
              followingCount={followingCount}
              collectionTypeCounts={collectionTypeCounts}
              filterLabel={getFilterLabel()}
              filterIcon={getFilterIcon()}
            />
          )}
        </div>
      </div>
    </div>
  );
});

interface TabButtonProps {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly label: string;
  readonly icon?: React.ReactNode;
}

const TabButton = memo(function TabButton({ active, onClick, label, icon }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex items-center gap-1.5",
        active
          ? "tab-active"
          : "border-transparent"
      )}
    >
      {icon}
      {label}
    </button>
  );
});

interface CollectionsFilterDropdownProps {
  readonly filter: CombinedCollectionFilter;
  readonly onFilterChange: (filter: CombinedCollectionFilter) => void;
  readonly collectionsCount: number;
  readonly followingCount: number;
  readonly collectionTypeCounts?: {
    private: number;
    collaborative: number;
    public: number;
  };
  readonly filterLabel: string;
  readonly filterIcon: React.ReactNode;
}

const CollectionsFilterDropdown = memo(function CollectionsFilterDropdown({
  filter,
  onFilterChange,
  collectionsCount,
  followingCount,
  collectionTypeCounts,
  filterLabel,
  filterIcon,
}: CollectionsFilterDropdownProps) {
  const isAllActive = filter === 'all';
  const isMyCollectionsActive = filter === 'mine' || filter.startsWith('mine-');
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-2 text-sm font-medium text-muted-foreground active:text-foreground transition-colors whitespace-nowrap">
          {filterIcon}
          {filterLabel}
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-2 bg-background border border-border shadow-xl rounded-2xl z-50">
        <DropdownMenuItem 
          onClick={() => onFilterChange('all')}
          className="rounded-xl py-2.5 px-3 flex items-center gap-3"
        >
          <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
            <Layers className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1">
            <span className="font-medium">All Collections</span>
            <p className="text-xs text-muted-foreground">Everything in one place</p>
          </div>
          {filter === 'all' && (
            <span className="ml-auto bg-primary px-2 py-0.5 rounded-full text-xs font-medium text-primary-foreground">
              {collectionsCount + followingCount}
            </span>
          )}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator className="my-2" />

        <DropdownMenuItem 
          onClick={() => onFilterChange('mine')}
          className="rounded-xl py-2.5 px-3 flex items-center gap-3"
        >
          <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
            <FolderOpen className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1">
            <span className="font-medium">My Collections</span>
            <p className="text-xs text-muted-foreground">Collections you created</p>
          </div>
          {filter === 'mine' && (
            <span className="ml-auto bg-primary px-2 py-0.5 rounded-full text-xs font-medium text-primary-foreground">
              {collectionsCount}
            </span>
          )}
        </DropdownMenuItem>
        
        {/* Sub-filters for My Collections */}
        <div className="pl-4 space-y-0.5">
          <DropdownMenuItem 
            onClick={() => onFilterChange('mine-private')}
            className="rounded-xl py-2 px-3 flex items-center gap-3"
          >
            <div className="w-6 h-6 rounded-full bg-muted/50 flex items-center justify-center">
              <Lock className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <span className="text-sm">Private</span>
            </div>
            {filter === 'mine-private' && collectionTypeCounts && (
              <span className="ml-auto bg-primary px-2 py-0.5 rounded-full text-xs font-medium text-primary-foreground">
                {collectionTypeCounts.private}
              </span>
            )}
          </DropdownMenuItem>
          
          <DropdownMenuItem 
            onClick={() => onFilterChange('mine-collaborative')}
            className="rounded-xl py-2 px-3 flex items-center gap-3"
          >
            <div className="w-6 h-6 rounded-full bg-blue-500/15 flex items-center justify-center">
              <Users className="w-3.5 h-3.5 text-blue-500" />
            </div>
            <div className="flex-1">
              <span className="text-sm">Group</span>
            </div>
            {filter === 'mine-collaborative' && collectionTypeCounts && (
              <span className="ml-auto bg-primary px-2 py-0.5 rounded-full text-xs font-medium text-primary-foreground">
                {collectionTypeCounts.collaborative}
              </span>
            )}
          </DropdownMenuItem>
          
          <DropdownMenuItem 
            onClick={() => onFilterChange('mine-public')}
            className="rounded-xl py-2 px-3 flex items-center gap-3"
          >
            <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center">
              <Globe className="w-3.5 h-3.5 text-emerald-500" />
            </div>
            <div className="flex-1">
              <span className="text-sm">Public</span>
            </div>
            {filter === 'mine-public' && collectionTypeCounts && (
              <span className="ml-auto bg-primary px-2 py-0.5 rounded-full text-xs font-medium text-primary-foreground">
                {collectionTypeCounts.public}
              </span>
            )}
          </DropdownMenuItem>
        </div>
        
        <DropdownMenuSeparator className="my-2" />
        
        <DropdownMenuItem 
          onClick={() => onFilterChange('following')}
          className="rounded-xl py-2.5 px-3 flex items-center gap-3"
        >
          <div className="w-8 h-8 rounded-full bg-pink-500/15 flex items-center justify-center">
            <FolderHeart className="w-4 h-4 text-pink-500" />
          </div>
          <div className="flex-1">
            <span className="font-medium">Following</span>
            <p className="text-xs text-muted-foreground">Collections by others</p>
          </div>
          {filter === 'following' && followingCount > 0 && (
            <span className="ml-auto bg-primary px-2 py-0.5 rounded-full text-xs font-medium text-primary-foreground">
              {followingCount}
            </span>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
